// Centralized pathway content. Neutral = default Prospera experience (also used for "explore" and pre-selection).

const NEUTRAL = {
  hero: {
    headlineClass: "text-[13vw] sm:text-[10vw] lg:text-[8.5vw]",
    lines: [
      { text: "Intelligence," },
      { text: "operationalized.", className: "italic text-cobalt" },
    ],
    support: null,
    body: "Prospera is not a traditional consultancy. We embed artificial intelligence into the core of how businesses operate — turning operational complexity into measurable, compounding advantage.",
    primaryCta: "Begin the conversation",
    secondaryCta: null,
  },
  problem: null,
  marquee: [
    "AI Strategy",
    "Process Automation",
    "Data Intelligence",
    "Digital Transformation",
    "Operational Excellence",
    "Decision Systems",
  ],
  services: {
    overline: "/ What we do",
    titleTop: "Four disciplines.",
    titleItalic: "One optimized enterprise.",
    intro: "Engaged individually or as an integrated program — each practice is designed to leave behind working systems, not slideware.",
    cards: [
      {
        no: "01",
        title: "AI Strategy & Roadmapping",
        desc: "We identify where intelligence creates leverage, then chart a pragmatic path from ambition to deployed capability — no theatre, only outcomes.",
        tags: ["Diagnostics", "Opportunity mapping", "Value modelling"],
      },
      {
        no: "02",
        title: "Intelligent Process Automation",
        desc: "We re-architect the workflows that quietly drain your margin, embedding automation and agents that run reliably at enterprise scale.",
        tags: ["Workflow design", "Agentic systems", "Integration"],
      },
      {
        no: "03",
        title: "Data & Decision Intelligence",
        desc: "We turn fragmented data into decision infrastructure — models and dashboards leaders actually trust when the stakes are highest.",
        tags: ["Data platforms", "Predictive models", "Decisioning"],
      },
      {
        no: "04",
        title: "Digital Transformation",
        desc: "We modernize operating models end-to-end, aligning technology, teams and incentives so transformation sticks rather than stalls.",
        tags: ["Operating models", "Change", "Enablement"],
      },
    ],
  },
  contact: {
    overline: "/ Start a conversation",
    heading: "Let's optimize",
    headingItalic: "what's next.",
    body: "Tell us where the friction lives in your business. We'll respond within two working days with a point of view — and, where it fits, a path to a first outcome.",
  },
};

const BUILD = {
  hero: {
    headlineClass: "text-[11vw] sm:text-[9vw] lg:text-[7.5vw]",
    lines: [
      { text: "Build a business" },
      { text: "that runs better.", className: "italic text-cobalt" },
    ],
    support: "Turn operational friction into capacity for growth.",
    body: "Prospera helps growing businesses identify what's holding them back, redesign how work gets done, and use automation and AI to create more capacity without adding unnecessary complexity.",
    primaryCta: "Find your highest-value opportunity",
    secondaryCta: { label: "See how we work", target: "approach" },
    img: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2000&auto=format&fit=crop",
    alt: "Builder overlooking an active construction site at dusk",
  },
  problem: {
    overline: "/ Where growth gets stuck",
    titleTop: "Where growth",
    titleItalic: "gets stuck.",
    items: [
      {
        no: "01",
        title: "Too much manual work",
        body: "Hours disappear into tasks that should run themselves — quietly capping how much your team can take on.",
      },
      {
        no: "02",
        title: "Inefficient processes",
        body: "Work moves through steps nobody designed, accumulating friction, delay and rework at every handoff.",
      },
      {
        no: "03",
        title: "Limited team capacity",
        body: "Your best people spend their days keeping things running instead of building what's next.",
      },
      {
        no: "04",
        title: "Operational bottlenecks",
        body: "Growth stalls in the same few places — approvals, handovers, and systems that don't talk to each other.",
      },
      {
        no: "05",
        title: "Growth is getting expensive",
        body: "Every new customer, order or hire costs more to serve than the last. Scale should work in your favor.",
      },
    ],
    closing: "We help you build the operating foundation for your next stage of growth — so scale creates margin, not chaos.",
  },
  marquee: [
    "Process Optimization",
    "Automation",
    "AI Integration",
    "Growth Infrastructure",
    "Operational Excellence",
    "Capacity by Design",
  ],
  services: {
    overline: "/ What we bring",
    titleTop: "Four disciplines.",
    titleItalic: "One business that runs better.",
    intro: "Engaged individually or as an integrated program — each practice builds capacity into the way your business runs.",
    cards: [
      {
        no: "01",
        title: "Process Optimization",
        desc: "We map how work actually gets done, strip out the friction, and redesign processes so growth stops creating drag.",
        tags: ["Diagnostics", "Workflow redesign", "Bottleneck removal"],
      },
      {
        no: "02",
        title: "Automation",
        desc: "We take the manual, repetitive work off your team's plate — automations that run reliably so people can do the work that matters.",
        tags: ["Task automation", "Agentic systems", "Integration"],
      },
      {
        no: "03",
        title: "AI Integration",
        desc: "We embed intelligence where it earns its keep — pragmatic AI that improves decisions and compounds value with every run.",
        tags: ["Applied AI", "Decision support", "Value modelling"],
      },
      {
        no: "04",
        title: "Growth Infrastructure",
        desc: "We build the operating foundation for your next stage — systems, data and processes that scale without adding headcount.",
        tags: ["Operating systems", "Data foundations", "Scalability"],
      },
    ],
  },
  contact: {
    overline: "/ Find your opportunity",
    heading: "Find your highest-value",
    headingItalic: "opportunity.",
    body: "Tell us where growth feels hardest right now. We'll respond within two working days with a point of view — and the single opportunity we'd pursue first.",
  },
};

const TRANSFORM = {
  hero: {
    headlineClass: "text-[11vw] sm:text-[9vw] lg:text-[7.5vw]",
    lines: [
      { text: "Transform how" },
      { text: "your organization" },
      { text: "operates.", className: "italic text-cobalt" },
    ],
    support: "Turn complexity into measurable performance.",
    body: "Prospera works with established organizations to redesign processes, integrate intelligent systems, and unlock operational value at scale.",
    primaryCta: "Identify transformation opportunities",
    secondaryCta: { label: "Explore our approach", target: "approach" },
    img: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?q=80&w=2000&auto=format&fit=crop",
    alt: "Monumental white architecture viewed from below",
  },
  problem: {
    overline: "/ The case for transformation",
    titleTop: "Complexity compounds.",
    titleItalic: "So does efficiency.",
    items: [
      {
        no: "01",
        title: "Operational complexity",
        body: "Layers of process, legacy and exception-handling accumulate until the organization spends more energy running itself than serving its market.",
      },
      {
        no: "02",
        title: "Inefficient processes",
        body: "Work crosses too many functions, systems and approvals — each handoff adding cost, delay and risk.",
      },
      {
        no: "03",
        title: "Fragmented systems",
        body: "Data lives everywhere and agrees nowhere. Decisions rely on reconciliation instead of intelligence.",
      },
      {
        no: "04",
        title: "Untapped transformation value",
        body: "The largest opportunities sit across functions, where no single owner can reach them — precisely where disciplined transformation pays.",
      },
    ],
    closing: "We work as a strategic partner to established organizations — redesigning how the enterprise operates so efficiency compounds the way complexity used to.",
  },
  marquee: [
    "Operational Transformation",
    "AI & Automation",
    "Decision Intelligence",
    "Operating Model Design",
    "Process Optimization",
    "Enterprise Value",
  ],
  services: {
    overline: "/ What we bring",
    titleTop: "Four disciplines.",
    titleItalic: "One transformed operation.",
    intro: "Engaged individually or as an integrated program — each practice unlocks measurable value across the operating model.",
    cards: [
      {
        no: "01",
        title: "Operational Transformation",
        desc: "We redesign how the enterprise operates end-to-end — simplifying complexity and rebuilding processes around value, not legacy.",
        tags: ["Process optimization", "Redesign", "Value capture"],
      },
      {
        no: "02",
        title: "AI & Automation",
        desc: "We embed intelligent automation across core operations — systems that run reliably at enterprise scale and improve with every cycle.",
        tags: ["Agentic systems", "Workflow automation", "Integration"],
      },
      {
        no: "03",
        title: "Decision Intelligence",
        desc: "We turn fragmented data into decision infrastructure — models and dashboards leaders trust when the stakes are highest.",
        tags: ["Data platforms", "Predictive models", "Decisioning"],
      },
      {
        no: "04",
        title: "Operating Model Design",
        desc: "We align structure, technology and incentives so transformation sticks — an operating model built for how you'll compete next.",
        tags: ["Operating models", "Governance", "Enablement"],
      },
    ],
  },
  contact: {
    overline: "/ Identify your opportunities",
    heading: "Identify transformation",
    headingItalic: "opportunities.",
    body: "Tell us where complexity costs you most. We'll respond within two working days with a point of view — and where we'd unlock value first.",
  },
};

const CONTENT = {
  neutral: NEUTRAL,
  explore: NEUTRAL,
  "build-scale": BUILD,
  "transform-optimize": TRANSFORM,
};

export const getContent = (perspective) => CONTENT[perspective] || CONTENT.neutral;
